import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, StatusBadge } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import {
  createResellerCustomer,
  fetchResellerCustomers,
  type CreateResellerCustomerInput,
  type ResellerCustomer,
} from '../api/endpoints.js';

export function ResellerCustomersPage() {
  const { id: resellerOrgId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const customers = useQuery({
    queryKey: ['resellerCustomers', resellerOrgId],
    queryFn: () => fetchResellerCustomers(resellerOrgId),
  });

  const columns: Column<ResellerCustomer>[] = [
    {
      key: 'name',
      header: 'Customer',
      render: (c) => c.customerDisplayName ?? c.customerOrgId,
    },
    { key: 'domain', header: 'Domain', render: (c) => c.customerPrimaryDomain ?? '—' },
    {
      key: 'markup',
      header: 'Markup %',
      render: (c) => (c.markupPercent != null ? `${c.markupPercent}%` : '—'),
    },
    {
      key: 'rel',
      header: 'Relationship',
      render: (c) => <StatusBadge status={c.relationshipStatus} />,
    },
    {
      key: 'manage',
      header: '',
      render: (c) => (
        <div className="row-actions">
          <Link
            className="btn btn--secondary btn--sm"
            to={`/tipsconnect/admin/customers/${c.customerOrgId}/users`}
            onClick={(e) => e.stopPropagation()}
          >
            Users
          </Link>
          <Link
            className="btn btn--secondary btn--sm"
            to={`/tipsconnect/admin/customers/${c.customerOrgId}/entitlements`}
            onClick={(e) => e.stopPropagation()}
          >
            Entitlements
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Reseller Customers"
        subtitle="Customer organizations managed by this reseller."
        leading={
          <Link className="back-arrow" to="/tipsconnect/admin/resellers">
            ‹ Resellers
          </Link>
        }
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            New Customer
          </button>
        }
      />

      <DataTable
        columns={columns}
        rows={customers.data}
        rowKey={(c) => c.id}
        isLoading={customers.isLoading}
        isError={customers.isError}
        error={customers.error}
        onRetry={() => void customers.refetch()}
        onRowClick={(c) => navigate(`/tipsconnect/admin/customers/${c.customerOrgId}/users`)}
        emptyMessage="This reseller has no customers yet."
      />

      <CreateCustomerModal
        resellerOrgId={resellerOrgId}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['resellerCustomers', resellerOrgId] });
        }}
      />
    </div>
  );
}

function CreateCustomerModal({
  resellerOrgId,
  open,
  onClose,
  onCreated,
}: {
  resellerOrgId: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateResellerCustomerInput>({
    legalName: '',
    displayName: '',
  });
  const mutation = useMutation({
    mutationFn: () => createResellerCustomer(resellerOrgId, form),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} title="New Reseller Customer" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Legal name">
          <input
            className="field__input"
            value={form.legalName}
            required
            onChange={(e) => setForm({ ...form, legalName: e.target.value })}
          />
        </Field>
        <Field label="Display name">
          <input
            className="field__input"
            value={form.displayName}
            required
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Field>
        <Field label="Primary domain" hint="Optional">
          <input
            className="field__input"
            value={form.primaryDomain ?? ''}
            onChange={(e) => setForm({ ...form, primaryDomain: e.target.value || undefined })}
          />
        </Field>
        <Field label="Markup %" hint="Optional">
          <input
            className="field__input"
            type="number"
            min={0}
            step="0.1"
            value={form.markupPercent ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                markupPercent: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </Field>
        <Field label="External customer ID" hint="Optional reseller-side reference">
          <input
            className="field__input"
            value={form.externalResellerCustomerId ?? ''}
            onChange={(e) =>
              setForm({ ...form, externalResellerCustomerId: e.target.value || undefined })
            }
          />
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
